import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Twitter, Linkedin, Youtube, Instagram, Music2, MessageCircle, Send, Link as LinkIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { trackEvent } from "@/lib/firebase";
import { OFFICIAL_SOCIALS, HEADQUARTERS } from "@/config/siteIdentity";
import { HeadquartersCards } from "@/components/SiteIdentity";

const COLS = [
  {
    title: "Explore",
    links: [
      ["About", "/about"],
      ["Heritage", "/heritage"],
      ["Global Network", "/global-network"],
      ["Articles", "/articles"],
      ["Blogs", "/blogs"],
    ],
  },
  {
    title: "Community",
    links: [
      ["Join", "/signup"],
      ["Organizations", "/organizations"],
      ["Opportunities", "/opportunities"],
      ["Events", "/events"],
      ["Login", "/login"],
    ],
  },
  {
    title: "Resources",
    links: [
      ["Media", "/media"],
      ["Contact", "/contact"],
      ["Global Network", "/global-network"],
      ["Initiatives", "/initiatives"],
    ],
  },
  {
    title: "Legal",
    links: [
      ["Privacy Policy", "/privacy"],
      ["Terms of Use", "/terms"],
      ["Cookie Policy", "/cookies"],
    ],
  },
];
const SOCIAL_ICONS = {
  facebook: Facebook,
  "x / twitter": Twitter,
  twitter: Twitter,
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
  tiktok: Music2,
  whatsapp: MessageCircle,
  telegram: Send,
  custom: LinkIcon,
};

export default function Footer() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [siteConfig, setSiteConfig] = useState(null);
  useEffect(() => {
    api
      .get("/site-config")
      .then(({ data }) => setSiteConfig(data))
      .catch(() => {});
  }, []);
  const footer = {
    ...(siteConfig?.footer || {}),
    ...(siteConfig?.builder?.footer || {}),
  };
  const branding = siteConfig?.branding || {};
  const columns = footer.groups?.length ? footer.groups : COLS;
  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter/subscribe", { email, first_name: name });
      trackEvent("newsletter_signup");
      toast.success("Subscribed! Welcome to DIR Nation.");
      setEmail("");
      setName("");
    } catch {
      toast.error("Subscription failed. Try again.");
    }
  };

  return (
    <footer
      className="border-t border-dir-gold/15 bg-dir-deep"
      data-testid="site-footer"
      style={{ backgroundColor: footer.background || undefined, color: footer.textColor || undefined }}
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10" style={{ paddingTop: footer.spacing || 64, paddingBottom: footer.spacing || 64 }}>
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-6 lg:gap-12">
          <div className="lg:col-span-2">
            <img
              src={
                footer.logo?.public_url ||
                branding.logo_url ||
                "/assets/dirnation-logo.png"
              }
              alt={`${branding.site_name || "DIR Nation"} — ${branding.tagline || "One People, Shared Future"}`}
              className="h-auto object-contain"
              style={{ width: footer.logoSize || 192, maxWidth: '100%' }}
            />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-dir-muted">
              {footer.description ||
                "A global platform connecting Dir people, communities, professionals, institutions and diaspora networks — preserving heritage and building opportunities for future generations."}
            </p>
            {(footer.socialLinks?.length ? footer.socialLinks : OFFICIAL_SOCIALS).some((x) => x.visible !== false && x.url) && <div className="mt-6 flex flex-wrap gap-3">
              {(footer.socialLinks?.length ? footer.socialLinks : OFFICIAL_SOCIALS).filter((x) => x.visible !== false && x.url).map((social, i) => {
                const Icon = SOCIAL_ICONS[(social.platform || 'custom').toLowerCase()] || LinkIcon;
                return (
                  <a
                    key={social.id || `${social.platform}-${i}`}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label || social.platform}
                    onClick={() => trackEvent("social_link_click", { platform: (social.platform || social.id || "custom").toLowerCase(), location: "footer" })}
                    data-testid={`social-${i}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-dir-gold/20 text-dir-muted hover:text-dir-gold hover:border-dir-gold/50 transition-colors"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>}
          </div>

          {columns.map((c, columnIndex) => (
            <div key={c.id || `${c.title}-${columnIndex}`}>
              <h4 className="text-sm font-semibold uppercase tracking-wider text-dir-gold">
                {c.title}
              </h4>
              <ul className="mt-4 space-y-2.5">
                {(c.links || []).filter((link) => Array.isArray(link) || link.visible !== false).map((link) => {
                  const [label, to] = Array.isArray(link)
                    ? link
                    : [link.label, link.url];
                  return (
                    <li key={link.id || `${label}-${to}`}>
                      {/^(https?:\/\/|mailto:|tel:)/i.test(to || '') ? <a
                        href={to}
                        target={!Array.isArray(link) && link.newTab ? '_blank' : undefined}
                        rel={!Array.isArray(link) && link.newTab ? 'noopener noreferrer' : undefined}
                        className="text-sm text-dir-muted hover:text-dir-text transition-colors"
                      >
                        {label}
                      </a> : <Link to={to || '#'} target={!Array.isArray(link) && link.newTab ? '_blank' : undefined} rel={!Array.isArray(link) && link.newTab ? 'noopener noreferrer' : undefined} className="text-sm text-dir-muted hover:text-dir-text transition-colors">{label}</Link>}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12"><HeadquartersCards headquarters={siteConfig?.identity?.headquarters || HEADQUARTERS} compact /></div>

        {footer.newsletter !== false && (
          <div className="mt-14 rounded-2xl gold-border bg-dir-card p-8">
            <div className="grid gap-6 md:grid-cols-2 md:items-center">
              <div>
                <h4 className="font-display text-xl font-semibold text-dir-text">
                  Stay Connected with DIR Nation
                </h4>
                <p className="mt-2 text-sm text-dir-muted">
                  Community news, opportunities and event invitations — direct
                  to your inbox.
                </p>
              </div>
              <form
                onSubmit={subscribe}
                className="flex flex-col gap-3 sm:flex-row"
                data-testid="footer-newsletter-form"
              >
                <input
                  data-testid="newsletter-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="First name"
                  className="w-full rounded-full bg-dir-elevated border border-dir-gold/20 px-5 py-3 text-sm text-dir-text placeholder:text-dir-faint focus:border-dir-gold focus:ring-2 focus:ring-dir-gold/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dir-gold"
                />
                <input
                  data-testid="newsletter-email"
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  className="w-full rounded-full bg-dir-elevated border border-dir-gold/20 px-5 py-3 text-sm text-dir-text placeholder:text-dir-faint focus:border-dir-gold focus:ring-2 focus:ring-dir-gold/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dir-gold"
                />
                <button
                  data-testid="newsletter-submit"
                  className="shrink-0 rounded-full bg-gradient-to-r from-dir-warm to-dir-gold px-6 py-3 text-sm font-bold text-dir-deep hover:brightness-110 transition-all"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-dir-gold/10 pt-8 text-center sm:flex-row sm:text-left">
          <p className="text-xs text-dir-faint">
            © {new Date().getFullYear()}{" "}
            {footer.copyright || "DIR Nation. All Rights Reserved."}
          </p>
          <p className="text-xs text-dir-faint">
            {footer.tagline || branding.tagline || "One People — Shared Future"}
          </p>
        </div>
      </div>
    </footer>
  );
}
